import React from 'react';
import { Card, CardBody, Button } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { refreshToken } from '../api/api';
import { setAuthorization } from '../api/httpClient';

interface RestrictionMessageProps {
  type: 'admin-no-company' | 'admin-incomplete-company' | 'user-no-company' | 'user-incomplete-company';
  navigate?: any;
}

interface MessageConfig {
  icon: string;
  iconBg: string;
  title: string;
  description: string;
  showButton: boolean;
  buttonColor?: string;
  buttonText?: string;
  buttonIcon?: string;
  action?: () => void;
}

const RestrictionMessage: React.FC<RestrictionMessageProps> = ({ type, navigate }) => {
  const routerNavigate = useNavigate();
  const navigation = navigate || routerNavigate;

  const handleRefreshToken = async () => {
    try {
      const authUser = localStorage.getItem("authUser");
      if (authUser) {
        const user = JSON.parse(authUser);
        if (user.token) {
          const refreshResponse = await refreshToken();
          
          if (refreshResponse && refreshResponse.token) {
            const newToken = refreshResponse.token;
            user.token = newToken;
            localStorage.setItem("authUser", JSON.stringify(user));
            setAuthorization(newToken);
            window.location.reload();
          }
        }
      }
    } catch (error) {
      console.error("Error al refrescar token:", error);
    }
  };

  const messages: Record<string, MessageConfig> = {
    'admin-no-company': {
      icon: 'ri-user-unfollow-line',
      iconBg: 'bg-soft-warning',
      buttonColor: 'warning',
      title: 'Sin Compañía Asignada',
      description: 'No tienes una compañía asignada actualmente. Contacta al Super Admin para que te asigne o cree una compañía.',
      buttonText: 'Actualizar',
      buttonIcon: 'ri-refresh-line',
      action: handleRefreshToken,
      showButton: true
    },
    'admin-incomplete-company': {
      icon: 'ri-building-4-line',
      iconBg: 'bg-soft-warning',
      buttonColor: 'warning',
      title: 'Completa la configuración de tu compañía',
      description: 'Para continuar utilizando todas las herramientas del sistema de facturación, primero debes completar la información de tu compañía.',
      buttonText: 'Configurar Compañía',
      buttonIcon: 'ri-building-4-line',
      action: () => navigation('/company-profile'),
      showButton: true
    },
    'user-no-company': {
      icon: 'ri-user-unfollow-line',
      iconBg: 'bg-soft-info',
      buttonColor: 'info',
      title: 'Sin Compañía Asignada',
      description: 'No tienes una compañía asignada actualmente. Por favor, contacta al administrador para que te asigne a una compañía.',
      buttonText: 'Actualizar',
      buttonIcon: 'ri-refresh-line',
      action: handleRefreshToken,
      showButton: true
    },
    'user-incomplete-company': {
      icon: 'ri-time-line',
      iconBg: 'bg-soft-info',
      buttonColor: 'info',
      title: 'Configuración en Progreso',
      description: 'El administrador está configurando la información de la compañía. Algunas funcionalidades estarán disponibles pronto.',
      buttonText: 'Verificar Estado',
      buttonIcon: 'ri-refresh-line',
      action: () => window.location.reload(),
      showButton: true
    }
  };

  const message = messages[type];

  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
      <Card className="text-center shadow-lg" style={{ maxWidth: '450px', border: 'none', borderRadius: '12px' }}>
        <CardBody className="p-5">
          <div className="avatar-lg mx-auto mb-4">
            <div className={`avatar-title ${message.iconBg} rounded-circle`}>
              <i className={`${message.icon} fs-24`}></i>
            </div>
          </div>
          
          <h4 className="mb-3 fw-semibold">{message.title}</h4>
          
          <p className="text-muted mb-4 px-3">
            {message.description}
          </p>

          {message.showButton && (
            <Button
              color={message.buttonColor!}
              onClick={message.action!}
              className="px-4"
              size="lg"
            >
              <i className={`${message.buttonIcon!} me-2`}></i>
              {message.buttonText!}
            </Button>
          )}
        </CardBody>
      </Card>
    </div>
  );
};

export default RestrictionMessage;
